import React, { useCallback } from 'react'

import type { InfiniteData } from '@tanstack/react-query'
import { Building2, Lock } from 'lucide-react'

import { useAuthInfo, useOrgHelper } from '@sweetauth/core'

import { NonAnimatedUsers } from '@/components/common/Avatar'
import { Badge } from '@/components/common/BadgeV2'

import { useOrganizationUsers } from '@/hooks/organization/useOrganizationUsers'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { User } from '@/types/User'

export type PursuitVisibilityChoice = 'public' | 'private'

// Stable empty fallback so avatar stacks don't get a fresh [] each render.
const EMPTY_USERS: User[] = []

interface WhoGetsAccessProps {
  orgId: UUID | ''
  visibility: PursuitVisibilityChoice
  selectedUserIds: string[]
}

/**
 * "Who gets access" preview under the visibility cards. Public shows the
 * org-wide audience; private shows you (plus any teammates already picked in
 * the members step) and points at that step for inviting.
 */
export const WhoGetsAccess: React.FC<WhoGetsAccessProps> = ({
  orgId,
  visibility,
  selectedUserIds,
}) => {
  const { user: currentUser } = useAuthInfo()
  const { orgHelper } = useOrgHelper()
  const currentUserId = (currentUser?.userId as UUID | undefined) ?? null
  const orgName = orgId ? orgHelper?.getOrg(orgId)?.orgName : undefined

  // You + your picks, for the private header stack.
  const selectMemberUsers = useCallback(
    (data: InfiniteData<PaginatedResponse<User>, PaginateRequest>) => {
      const selectedSet = new Set(selectedUserIds)
      return data.pages
        .flatMap((res) => res.data)
        .filter((user) => user.id === currentUserId || selectedSet.has(user.id))
    },
    [selectedUserIds, currentUserId],
  )
  const { data: memberUsersData } = useOrganizationUsers({
    orgId,
    select: selectMemberUsers,
  })
  const { data: orgUsersData } = useOrganizationUsers({ orgId })
  const memberUsers = memberUsersData ?? EMPTY_USERS
  const orgUsers = orgUsersData ?? EMPTY_USERS

  // Don't count yourself among the invited teammates: the details step's
  // Owners field writes into the same user_ids and can include the creator,
  // and "You + N" already names you separately.
  const selectedCount = selectedUserIds.filter(
    (id) => id !== currentUserId,
  ).length

  return (
    <div className="space-y-2">
      <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-primary-500 dark:text-primary-400">
        Who gets access
      </div>
      {/* Panel chrome + type ramp match the SharePoint import panel on the
          details step (rounded-lg, tinted bg, text-sm/text-xs); avatars match
          the member-picker rows (28px). */}
      {visibility === 'public' ? (
        <div
          key="public"
          className="flex items-center gap-3 rounded-lg border border-primary-200 bg-primary-50/50 px-4 py-3 animate-in fade-in-0 slide-in-from-bottom-1 duration-200 dark:border-primary-800 dark:bg-primary-900/30"
        >
          {orgUsers.length > 0 && (
            <NonAnimatedUsers
              users={orgUsers}
              limit={4}
              width={28}
              height={28}
              className="shrink-0"
            />
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-primary-900 dark:text-primary-100">
              {orgName
                ? `Everyone at ${orgName}`
                : 'Everyone in your organization'}
            </div>
            <p className="mt-0.5 text-xs leading-4 text-primary-500 dark:text-primary-400">
              {orgUsers.length > 0
                ? `${orgUsers.length} teammate${orgUsers.length === 1 ? '' : 's'} get access · anyone can view and edit`
                : 'Anyone in your organization can view and edit'}
            </p>
          </div>
          <Badge
            variant="blue"
            className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px]"
          >
            <Building2 className="h-3 w-3" />
            Org-wide
          </Badge>
        </div>
      ) : (
        <div
          key="private"
          className="flex items-center gap-3 rounded-lg border border-primary-200 bg-primary-50/50 px-4 py-3 animate-in fade-in-0 slide-in-from-bottom-1 duration-200 dark:border-primary-800 dark:bg-primary-900/30"
        >
          {memberUsers.length > 0 && (
            <NonAnimatedUsers
              users={memberUsers}
              limit={4}
              width={28}
              height={28}
              className="shrink-0"
            />
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-primary-900 dark:text-primary-100">
              {selectedCount === 0
                ? 'Just you, for now'
                : `You + ${selectedCount} teammate${selectedCount === 1 ? '' : 's'}`}
            </div>
            <p className="mt-0.5 text-xs leading-4 text-primary-500 dark:text-primary-400">
              {selectedCount === 0
                ? "You'll pick the teammates to invite in the next step."
                : 'Invited teammates get full access and appear as owners.'}
            </p>
          </div>
          <Badge
            variant="amber"
            className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px]"
          >
            <Lock className="h-3 w-3" />
            Invite only
          </Badge>
        </div>
      )}
    </div>
  )
}

WhoGetsAccess.displayName = 'CreatePursuitDialog.WhoGetsAccess'
